import { useMemo, useState } from "react";
import { Pencil } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Card } from "@/shared/ui/card";
import {
  SortableTableHead,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import { useLanguage } from "@/shared/hooks/use-language";
import { useNumberFormat } from "@/shared/hooks/use-number-format";
import { formatAmountScale, formatPercent } from "@/shared/lib/amount";
import { labelOr } from "@/shared/lib/label";
import { instrumentTypeLabelKey } from "@/shared/lib/instrument-type";
import {
  nextPositionSort,
  positionRanking,
  sortPositionRows,
  type PositionRankingCaveat,
  type PositionRow,
  type PositionSort,
  type PositionSortColumn,
} from "@/features/portfolio/lib/position-sort";
import { consolidationStateOf } from "@/features/portfolio/api/position";
import type { Position, PositionSummary } from "@/features/portfolio/api/position";
import { bookingStateOf, type BookingStatus } from "@/features/portfolio/hooks/use-booking-status";
import { emptyPositionsCopy } from "@/features/portfolio/lib/booking-label";

const CAVEAT_KEY: Record<PositionRankingCaveat, string> = {
  "weights-per-currency": "portfolio.weightsPerCurrency",
  "zero-total": "portfolio.weightsZeroTotal",
};

const CAVEAT_TEST_ID: Record<PositionRankingCaveat, string> = {
  "weights-per-currency": "positions-weights-per-currency",
  "zero-total": "positions-weights-zero-total",
};

interface PositionsTableProps {
  positions: Position[];
  summary: PositionSummary | null;
  isLoading: boolean;
  onDeclare?: (position: Position) => void;
  /** Whether the rows are final — see `useBookingStatus`. Read only by the empty state,
   *  which must not call a book with rows still booking "no positions". */
  booking: BookingStatus;
}

export const PositionsTable = ({ positions, summary, isLoading, onDeclare, booking }: PositionsTableProps) => {
  const { t, language } = useLanguage();
  const { numberFormat } = useNumberFormat();
  // The reader's own pick, `null` until they make one. It is an input to the derivation
  // below, never the answer: a pick made while the weights were rankable does not stay
  // true after a refresh that empties them.
  const [chosenSort, setChosenSort] = useState<PositionSort | null>(null);

  const unsortedRows: PositionRow[] = useMemo(() => {
    const weightByPositionId = new Map(
      (summary?.positions || []).map((entry) => [entry.id, entry.weight ?? null]),
    );
    return positions.map((position) => ({
      position,
      classLabel: labelOr(t, instrumentTypeLabelKey(position.instrument_type), position.instrument_type),
      weight: weightByPositionId.get(position.id) ?? null,
    }));
  }, [positions, summary, t]);

  // One question, asked of the rows on screen and asked again whenever they change: is the
  // order this table is showing an honest one? Its answer is the order, the direction each
  // header reports, and the line above the rows — so the three cannot disagree, and none
  // of them can go on describing data that has since been replaced.
  const consolidation = consolidationStateOf(summary);
  const { sort, caveat } = useMemo(
    () => positionRanking({ rows: unsortedRows, consolidation, summary, chosenSort }),
    [unsortedRows, consolidation, summary, chosenSort],
  );

  // Held rows reordered in place — a header click never refetches, because the whole
  // portfolio is already here (see `getAllPositions`) and the columns it ranks are
  // computed at read time, so no server order could answer the click anyway.
  const rows = useMemo(
    () => (sort == null ? unsortedRows : sortPositionRows(unsortedRows, sort, language)),
    [unsortedRows, sort, language],
  );

  const sortableHead = (column: PositionSortColumn, label: string, align: "left" | "right") => (
    <SortableTableHead
      label={label}
      direction={sort?.column === column ? sort.direction : null}
      onSort={() => setChosenSort(nextPositionSort(sort, column))}
      testId={`position-sort-${column}`}
      align={align}
    />
  );

  // An empty table before the booking check has answered once is not yet a fact of any
  // kind — settled, booking or unknown — so it waits behind the same skeleton as the
  // rows themselves rather than flashing "no positions" until the first answer lands.
  // `hasAnswered` is the hook's own flag for this rule — the NAV and position-count
  // cards (`NavHeader`) hold their figures behind the same flag for the same reason.
  const awaitsFirstBookingAnswer = positions.length === 0 && !booking.hasAnswered;

  if (isLoading || awaitsFirstBookingAnswer) {
    return (
      <Card className="p-6">
        <div className="skeleton-shimmer h-4 w-full rounded bg-muted mb-3" />
        <div className="skeleton-shimmer h-4 w-5/6 rounded bg-muted mb-3" />
        <div className="skeleton-shimmer h-4 w-4/6 rounded bg-muted" />
      </Card>
    );
  }

  // An empty table is several different facts, and the plain "no positions" one is the
  // only one that tells the reader to go record something. Keyed off the same
  // `bookingStateOf` the marker above the table uses, so the two can never disagree: a
  // settling, slow, or timed-out book says so with its counts (`emptyPositionsCopy`,
  // which also picks the right tail for each of those three — see its own doc comment).
  // An unknown check — the check itself failed — gets its own card: the full marker
  // above the table already says the check failed, and this card must not repeat "no
  // positions" as a claim it cannot make. An incomplete check is the opposite case: its
  // trade and snapshot reads answered empty with no cursor, so the table is proven
  // empty and the plain card is the honest one — only the running imports went
  // unchecked, and the full marker above says so.
  if (positions.length === 0) {
    const bookingState = bookingStateOf(booking);
    if (bookingState === "unknown") {
      return (
        <Card className="p-10 text-center text-muted-foreground" data-testid="positions-empty-unknown">
          {t("portfolio.positionsUnknown")}
        </Card>
      );
    }
    if (bookingState != null && bookingState !== "incomplete" && booking.counts != null) {
      return (
        <Card
          className="p-10 text-center text-muted-foreground"
          data-testid="positions-empty-booking"
          data-booking-state={bookingState}
        >
          {emptyPositionsCopy(t, bookingState, booking.counts, numberFormat)}
        </Card>
      );
    }
    return (
      <Card className="p-10 text-center text-muted-foreground" data-testid="positions-empty">
        {t("portfolio.positionsEmpty")}
      </Card>
    );
  }

  return (
    <Card data-testid="positions-table">
      {/* Above the rows it qualifies, so the reader meets the qualification before the
          first percentage rather than after the last one. */}
      {caveat && (
        <p
          className="px-4 pt-4 text-sm text-muted-foreground"
          data-testid={CAVEAT_TEST_ID[caveat]}
        >
          {t(CAVEAT_KEY[caveat])}
        </p>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            {sortableHead("instrument", t("portfolio.col.instrument"), "left")}
            {sortableHead("class", t("portfolio.col.type"), "left")}
            {sortableHead("quantity", t("portfolio.col.quantity"), "right")}
            {sortableHead("price", t("portfolio.col.price"), "right")}
            {sortableHead("value", t("portfolio.col.value"), "right")}
            {sortableHead("weight", t("portfolio.col.weight"), "right")}
            {onDeclare && (
              <TableHead className="w-12">
                <span className="sr-only">{t("portfolio.col.actions")}</span>
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(({ position, classLabel, weight }) => (
            <TableRow key={position.id} data-testid="position-row">
              <TableCell className="font-medium" data-testid="position-symbol">
                {position.symbol || position.instrument_id}
              </TableCell>
              <TableCell>{classLabel}</TableCell>
              <TableCell className="text-right tabular-nums" data-testid="position-quantity">
                {formatAmountScale(position.symbol_amount, position.symbol_scale, numberFormat, { trimZeros: true })}
              </TableCell>
              <TableCell
                className="text-right tabular-nums"
                data-testid="position-price"
                title={position.reference_time || undefined}
              >
                {formatAmountScale(position.current_price_amount, position.current_price_scale, numberFormat)}
                {position.currency ? ` ${position.currency}` : ""}
              </TableCell>
              <TableCell className="text-right tabular-nums" data-testid="position-value">
                {formatAmountScale(position.total_amount, position.total_scale, numberFormat)}
              </TableCell>
              <TableCell className="text-right tabular-nums" data-testid="position-weight">
                {formatPercent(weight, numberFormat)}
              </TableCell>
              {onDeclare && (
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t("portfolio.snapshotEdit")}
                    data-testid="position-edit"
                    onClick={() => onDeclare(position)}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};
